import {
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {gsap} from "gsap";
import {Game} from "../Game";
import {SymbolContainer} from "../reels/SymbolContainer";
import {GameUtils} from "../utils/GameUtils";
import {SYMBOL} from "../enums/Enum";
import {Symbol} from "../reels/Symbol";
import {Config} from "../config/Config";
import {Sound} from "../sound/Sound";

export class S2RevealRunner implements SimplePresentationRunner {
    private _xSplitCount: number;
    private _index: number = 0;
    private _mainTl: gsap.core.Timeline;

    shouldRun(): boolean {
        return Game.model.s2InReels[0] != -1 || Game.model.s2InReels[1] != -1;
    }

    start(flowStateController: SimplePresentationRunnerController): void {
        this._index = 0;
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (Game.model.isSplitPresentInS2Reveal()) {
            tl.add(() => this.playS2RevealSequentially(flowStateController));
        } else {
            tl.add(() => this.playS2Reveal(flowStateController));
        }

    }

    private playS2Reveal(flowStateController: SimplePresentationRunnerController): void {
        this._mainTl = new gsap.core.Timeline();
        Game.model.s2InReels.forEach((rowId: number, index: number) => {
            const s2ReelId: number = index === 0 ? 1 : 3;
            const featureTriggeredArr = index === 0 ? Game.model.gameData.firstS2FeatureTriggered : Game.model.gameData.secondS2FeatureTriggered;
            if (rowId != -1) {
                this._mainTl.add(() => {
                    flowStateController.setSkipCallback(() => {
                        this.onSkip();
                    });
                });
                this._mainTl.add(this.reveal(s2ReelId, rowId, featureTriggeredArr), index * 0.2);
            }
        })
        this._mainTl.add(() => {
            this.onComplete(flowStateController)
        });
    }

    private onComplete(flowStateController: SimplePresentationRunnerController): void {
        flowStateController.finish();
    }

    private addSplitRow(splitRowArr: number[], rowId: number) {
        if (Game.model.isStickyDeadApplicable()) {
            if (splitRowArr.indexOf(rowId) === -1) {
                splitRowArr.push(rowId);
            }
        }
    }

    private playS2RevealSequentially(flowStateController: SimplePresentationRunnerController): void {
        this._mainTl = new gsap.core.Timeline();
        const splitRowArr: number[] = [];
        let rowId: number = Game.model.s2InReels[this._index];
        const s2ReelId: number = this._index === 0 ? 1 : 3;
        const featureTriggeredArr = this._index === 0 ? Game.model.gameData.firstS2FeatureTriggered : Game.model.gameData.secondS2FeatureTriggered;
        if (rowId != -1) {
            this._mainTl.add(() => {
                flowStateController.setSkipCallback(() => {
                    this.onSkip();
                });
            });
            this._mainTl.add(this.reveal(s2ReelId, rowId, featureTriggeredArr));
            if (featureTriggeredArr.indexOf(SYMBOL.X_SPLIT) > -1) {
                this.addSplitRow(splitRowArr, rowId);
            }
        }
        this._index++;
        if (this._index < 2) {
            this._mainTl.add(() => {
                const stickyTl: gsap.core.Timeline = new gsap.core.Timeline();
                splitRowArr.forEach((rowId: number) => {
                    for (let reelId: number = 0; reelId < 5; reelId++) {
                        stickyTl.add(Game.stickyView.checkAndSetStickyDeadMultiplierToSymbol(GameUtils.getSymbol(reelId, rowId)), 0);
                    }
                });
                stickyTl.add(() => {
                    this.playS2RevealSequentially(flowStateController)
                });
            });
        } else {
            this._mainTl.add(() => {
                flowStateController.finish();
            });
        }
    }


    private reveal(s2ReelId: number, rowId: number, featureTriggeredArr: string[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const symbolContainer: SymbolContainer = GameUtils.getSymbol(s2ReelId, rowId);
        tl.add(() => {
            Game.soundPlayer.playSound(Sound.S2_TRANSFORM)
        }, 0);
        symbolContainer.symbols.forEach((sym: Symbol, subRowId: number) => {
            tl.add(symbolContainer.symChangeEffect(subRowId), 0);
            tl.add(symbolContainer.changeSymbol(subRowId, featureTriggeredArr[subRowId]), 0.7);
            if (featureTriggeredArr[subRowId] === SYMBOL.X_WAYS) {
                tl.add(() => {
                    Game.foregroundElements.featureLogoContainer.playXWaysActive();
                })
            }
            if (Game.model.isStickyDeadApplicable() && Game.model.stickyDeadSymInfoCopy[featureTriggeredArr[subRowId]]) { //distribute sticky dead multiplier
                let multTl: gsap.core.Timeline;
                tl.add(() => {
                    multTl = Game.stickyView.sendMultiplierToSpecificSymbol(s2ReelId, rowId, subRowId, featureTriggeredArr[subRowId]);
                });
                tl.to({}, {duration: 0.25});
                tl.add(() => {
                    multTl?.totalProgress(1);
                });
            }
            if (featureTriggeredArr[subRowId] === SYMBOL.DEAD && Game.model.deadSymCount > 1) {
                tl.add(() => {
                    Game.reelView.doActivateDeadSymbols()
                }, 0.7);
            }
        });

        //Xsplit from S2
        if (featureTriggeredArr.indexOf(SYMBOL.X_SPLIT) > -1) {
            tl.add(() => {
                Game.foregroundElements.featureLogoContainer.playXSplitActive()
            }, 0.4);
            const splitTriggeredSymbolContainer: SymbolContainer = GameUtils.getSymbol(s2ReelId, rowId);

            featureTriggeredArr.forEach((feature: string, subRowId: number) => {
                this._xSplitCount++;
                const featureTl: gsap.core.Timeline = new gsap.core.Timeline();
                if (feature === SYMBOL.X_SPLIT) {
                    featureTl.add(() => {
                        subRowId = splitTriggeredSymbolContainer.getLatestSplitIndex();
                        const splitSymbol: Symbol = splitTriggeredSymbolContainer.symbols[subRowId];
                        const animNameArr: string[] = (splitSymbol.state.tracks[0].animation.name).split("_");
                        const animName: string = animNameArr[animNameArr.length - 1];
                        splitSymbol.state.setAnimation(0, animName + "_without_logo", false);
                        splitSymbol.symbolName = SYMBOL.WILD;
                    }, 0);
                    for (let reelId: number = 0; reelId < 5; reelId++) {
                        const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
                        if (symbolContainer.symbolName == SYMBOL.X_MENTAL ||  symbolContainer.symbolName == SYMBOL.S1) { //#432
                            continue;
                        }
                        Game.foregroundElements.waysCounter.symCountInReels[reelId]++;
                        const pos: PIXI.Point = GameUtils.getSymbolPos(reelId, rowId);
                        const isXHoleSym: boolean = symbolContainer.symbolName === SYMBOL.X_HOLE;
                        if (symbolContainer.isTopHalf) {
                            pos.y = pos.y - 49;
                        }
                        pos.y -= symbolContainer.pivot.y;

                        let animName: string = this._xSplitCount > 2 ? "50_cut_effect" : (this._xSplitCount === 2) ? "99_cut_effect" : "198_cut_effect";
                        const speed: number = 2.2;//this._xSplitCount > 2 ? 2.8 : (this._xSplitCount === 2) ? 2.6 : 2.2;
                        const splitEffects: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xSplitEffects"));
                        const xFeatureOverlaySpine: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xSplitWinOverlay"));
                        const xSpitBloodDripSpine: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xSpitBloodDripEffects"));

                        Game.effectsView.addChild(splitEffects);
                        if (isXHoleSym) {
                            animName = Game.model.isXHoleActive() ? "198_cut_effect_suck" : "198_cut_effect_suck_noWin";
                            xFeatureOverlaySpine.visible = false;
                            xSpitBloodDripSpine.visible = false;
                        }
                        const xSplitAnim = GameUtils.createTimelineForAnimation(splitEffects, 0, animName, false).timeScale(speed);
                        if (reelId === 0) {
                            xSplitAnim.add(() => {
                                Game.soundPlayer.playSound(Sound.X_SPLIT_START);
                                Game.soundPlayer.playSound(Sound.X_SPLIT_SPLITTING);
                            }, 0);
                            xSplitAnim.add(() => {
                                Game.soundPlayer.stopSound(Sound.X_SPLIT_SPLITTING);
                                Game.soundPlayer.playSound(Sound.X_SPLIT_END);
                            });
                        }

                        featureTl.add(xSplitAnim, 0);

                        featureTl.add(() => {
                            // if (reelId === 0) {
                            //     Game.soundPlayer.playSound(Sound.X_SPLIT_START);
                            //     Game.soundPlayer.playSound(Sound.X_SPLIT_SPLITTING);
                            // }
                            let symIndex: number = Math.min(subRowId, symbolContainer.symbols.length - 1);
                            const symAtSubRowId: Symbol = symbolContainer.symbols[symIndex];
                            splitEffects.position.set(pos.x, pos.y + symAtSubRowId.y);
                            xFeatureOverlaySpine.position.set(pos.x, pos.y + symAtSubRowId.y);
                            xSpitBloodDripSpine.position.x = (pos.x);
                            xFeatureOverlaySpine.height = symAtSubRowId.currentHeight * 1.62;
                            const symHeight: number = Config.SYMBOL_HEIGHT / (symbolContainer.symbols.length + 1);
                            if (symbolContainer.isTopHalf) { //Handle top half Symbol
                                symIndex += symbolContainer.symbols.length;
                            }
                            const y: number = (symHeight * (symIndex + 1)) - 99;
                            xSpitBloodDripSpine.y = pos.y + y;
                            xSpitBloodDripSpine.scale.y = (symHeight * 2) / 202;
                        }, 0);


                        featureTl.add([
                            () => {
                                Game.effectsView.addChild(xSpitBloodDripSpine, xFeatureOverlaySpine);
                                // if (reelId === 0) {
                                //     Game.soundPlayer.stopSound(Sound.X_SPLIT_SPLITTING);
                                //     Game.soundPlayer.playSound(Sound.X_SPLIT_END);
                                // }
                            },
                            GameUtils.createTimelineForAnimation(xFeatureOverlaySpine, 0, "198_win", false),
                            GameUtils.createTimelineForAnimation(xSpitBloodDripSpine, 0, "198_win", false)
                        ], 0.8 - (speed - 1.64));
                        featureTl.add(() => {
                            if (Game.model.gameData.evaluatedArea[reelId][rowId] !== SYMBOL.X_HOLE && Game.model.gameData.evaluatedArea[reelId][rowId] !== SYMBOL.X_MENTAL) {
                                symbolContainer.splitByFeature(subRowId);
                            }
                        }, 1.2 - (speed - 1.64));
                        featureTl.add(() => {
                            splitEffects.parent?.removeChild(splitEffects);
                            xFeatureOverlaySpine.parent?.removeChild(xFeatureOverlaySpine);
                            xSpitBloodDripSpine.parent?.removeChild(xFeatureOverlaySpine);
                        });
                    }
                    featureTl.add(() => {
                        Game.foregroundElements.waysCounter.updatePossibleWaysCounter();
                    })
                }
                tl.add(featureTl);
            });
        }
        return tl;
    }

    private onSkip() {
        this._mainTl.timeScale(10);
        Game.soundPlayer.stopSound(Sound.S2_TRANSFORM);
    }
}